import { Router } from '@angular/router';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LayoutConfigService } from '@red/components/layout-config';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Layout, LayoutConfig } from './layout.types';

@Component({
  selector: 'red-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LayoutBaseComponent implements OnInit {
  layout!: Layout;
  config!: LayoutConfig;
  private _unsubscribeAll: Subject<unknown> = new Subject<unknown>();
  constructor(protected _router: Router, private _activatedRoute: ActivatedRoute, private configService: LayoutConfigService) {}

  ngOnInit(): void {
    // Subscribe to config changes
    this.configService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: LayoutConfig) => {
      // Store the config
      this.config = config;

      // Update the layout
      this._updateLayout();
    });
  }

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout') as Layout;
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }
    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach(path => {
      // Check if there is a 'layout' data
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data['layout']) {
        // Set the layout
        this.layout = path.routeConfig.data['layout'];
      }
    });
  }
}
